import React, { useEffect, useState } from 'react';
import AdminNavbar from '../../component/adminNavbar';
import {
	Box,
	Table,
	Thead,
	Tbody,
	Tr,
	Th,
	Td,
	Heading,
	Input,
	Button,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalCloseButton,
	ModalFooter,
	FormControl,
	FormLabel,
	Select,
	ButtonGroup,
	HStack,
	SimpleGrid,
	Spinner,
	Stack,
	Flex,
	Text,
	Badge,
} from '@chakra-ui/react';
import { connect } from 'react-redux';
import {
	getAllFacilities,
	saveFacility,
	editFacility,
} from '../../redux/actions/facilityActions';
import {
	useReactTable,
	createColumnHelper,
	flexRender,
	getCoreRowModel,
} from '@tanstack/react-table';
import Alert from '../../component/alert';
import { EditIcon, DeleteIcon, AddIcon, SearchIcon } from '@chakra-ui/icons';
import { states } from '../../utils/states';
import axios from 'axios';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';
const authConfig = () => ({ headers: { 'x-auth-token': localStorage.getItem('x-auth-token') } });

const Facilities = ({
	getAllFacilities,
	editFacility,
	facilities,
	loading,
	saveFacility,
	count,
}) => {
	const [offset, setOffset] = useState(0);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [nameFilter, setNameFilter] = useState('');
	const [isSearching, setIsSearching] = useState(false);
	const [name, setName] = useState('');
	const [state, setState] = useState('');
	const [address, setAddress] = useState('');
	const [city, setCity] = useState('');
	const [zip, setZip] = useState('');
	const [priceGroupId, setPriceGroupId] = useState('');
	const [fishbowlCustomerName, setFishbowlCustomerName] = useState('');
	const [fishbowlTaxRateName, setFishbowlTaxRateName] = useState('');
	const [priceGroups, setPriceGroups] = useState<any[]>([]);
	const [selectedFacility, setSelectedFacility] = useState(null);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [facilityToDelete, setFacilityToDelete] = useState(null);

	useEffect(() => {
		getAllFacilities(offset);
	}, [offset]);

	useEffect(() => {
		loadPriceGroups();
	}, []);

	const handleSearchClick = () => {
		setNameFilter((v) => v.trim());
		setOffset(0);
		setIsSearching(true);
	};

	const handleClearClick = () => {
		setNameFilter('');
		setOffset(0);
		setIsSearching(true);
	};

	const Tabs = ({ count, onOffsetChange, offset }) => {
		const groupSize = 25;
		const numPages = Math.ceil(count / groupSize);
		const currentPage = Math.floor(offset / groupSize);

		if (numPages <= 1) return null;

		const pages: (number | 'ellipsis')[] = [0];
		const rangeStart = Math.max(1, currentPage - 1);
		const rangeEnd = Math.min(numPages - 2, currentPage + 1);
		if (rangeStart > 1) pages.push('ellipsis');
		for (let i = rangeStart; i <= rangeEnd; i++) pages.push(i);
		if (rangeEnd < numPages - 2) pages.push('ellipsis');
		if (numPages > 1) pages.push(numPages - 1);

		return (
			<Flex mt={6} justify="center" align="center" gap={1}>
				<Button size="sm" variant="outline" onClick={() => onOffsetChange(Math.max(0, offset - groupSize))} isDisabled={currentPage === 0}>‹</Button>
				{pages.map((page, idx) =>
					page === 'ellipsis' ? (
						<Text key={`e-${idx}`} px={2} color="gray.400">…</Text>
					) : (
						<Button key={page} size="sm" colorScheme={currentPage === page ? 'blue' : 'gray'} variant={currentPage === page ? 'solid' : 'outline'} onClick={() => onOffsetChange((page as number) * groupSize)}>
							{(page as number) + 1}
						</Button>
					)
				)}
				<Button size="sm" variant="outline" onClick={() => onOffsetChange(Math.min((numPages - 1) * groupSize, offset + groupSize))} isDisabled={currentPage === numPages - 1}>›</Button>
			</Flex>
		);
	};

	const loadPriceGroups = async () => {
		try {
			const res = await axios.get(`${BASE_URL}/api/price-groups`, authConfig());
			setPriceGroups(res.data || []);
		} catch {
			setPriceGroups([]);
		}
	};

	const handleOpenModal = async (facility?) => {
		await loadPriceGroups();
		if (facility) {
			setSelectedFacility(facility);
			setName(facility.name);
			setState(facility.state);
			setAddress(facility.address);
			setCity(facility.city || '');
			setZip(facility.zip || '');
			setPriceGroupId(facility.price_group_id ? String(facility.price_group_id) : '');
			setFishbowlCustomerName(facility.fishbowl_customer_name || '');
			setFishbowlTaxRateName(facility.fishbowl_tax_rate_name || '');
		} else {
			setSelectedFacility(null);
			setName('');
			setState('');
			setAddress('');
			setCity('');
			setZip('');
			setPriceGroupId('');
			setFishbowlCustomerName('');
			setFishbowlTaxRateName('');
		}
		setIsModalOpen(true);
	};

	const handleCloseModal = () => {
		setIsModalOpen(false);
		setSelectedFacility(null);
	};

	const handleOpenDeleteModal = (facility) => {
		setFacilityToDelete(facility);
		setIsDeleteModalOpen(true);
	};

	const handleCloseDeleteModal = () => {
		setFacilityToDelete(null);
		setIsDeleteModalOpen(false);
	};

	const confirmDelete = async () => {
		if (facilityToDelete) {
			const success = await handleSave({ ...facilityToDelete, deleted: 0 });
			if (success) handleCloseDeleteModal();
		}
	};

	const handleSave = async (data?) => {
		const facilityData = data || {
			name,
			state,
			address,
			city: city.trim(),
			zip: zip.trim(),
			price_group_id: priceGroupId ? parseInt(priceGroupId) : null,
			fishbowl_customer_name: fishbowlCustomerName.trim() || null,
			fishbowl_tax_rate_name: fishbowlTaxRateName.trim() || null,
			deleted: selectedFacility?.deleted ?? 1,
		};

		if (
			facilityData.deleted !== 0 &&
			(!facilityData.name?.trim() ||
				!facilityData.state?.trim() ||
				!facilityData.address?.trim() ||
				!facilityData.city?.trim() ||
				!facilityData.zip?.trim())
		) {
			return false;
		}

		let success = false;
		const id = data?.facilityId || selectedFacility?.facilityId;

		if (id) {
			facilityData.facilityId = id;
			success = await editFacility(facilityData);
		} else {
			try {
				await saveFacility(facilityData);
				success = true;
			} catch (error) {
				success = false;
			}
		}

		if (success) {
			getAllFacilities(offset);
			handleCloseModal();
		}
		return success;
	};

	const columnHelper = createColumnHelper<any>();
	const columns = [
		columnHelper.accessor('facilityId', {
			cell: (info) => info.getValue(),
			header: 'Facility ID',
		}),
		columnHelper.accessor('name', {
			cell: (info) => info.getValue(),
			header: 'Name',
		}),
		columnHelper.accessor('state', {
			cell: (info) => info.getValue(),
			header: 'State',
		}),
		columnHelper.accessor('city', {
			cell: (info) => info.getValue() || '—',
			header: 'City',
		}),
		columnHelper.accessor('zip', {
			cell: (info) => info.getValue() || '—',
			header: 'Zip',
		}),
		columnHelper.accessor('address', {
			cell: (info) => info.getValue(),
			header: 'Address',
		}),
		columnHelper.accessor('price_group_id', {
			header: 'Price Group',
			cell: (info) => {
				const pgId = info.getValue();
				const group = priceGroups.find((g) => g.id === pgId);
				return group ? <Badge colorScheme="blue">{group.name}</Badge> : <Text fontSize="xs" color="gray.400">—</Text>;
			},
		}),
		columnHelper.display({
			id: 'action',
			header: 'Action',
			cell: ({ row }) => (
				<HStack spacing={2}>
					<ButtonGroup size="xs">
						<Button
							colorScheme="blue"
							onClick={() => handleOpenModal(row.original)}
						>
							<EditIcon />
						</Button>
						<Button
							colorScheme="red"
							onClick={() => handleOpenDeleteModal(row.original)}
						>
							<DeleteIcon />
						</Button>
					</ButtonGroup>
				</HStack>
			),
		}),
	];

	const table = useReactTable({
		columns,
		data: facilities || [],
		getCoreRowModel: getCoreRowModel(),
	});

	return (
		<>
			<Alert />
			<AdminNavbar />
			{!loading ? (
				<Box pt={{ base: 16, md: 6 }} px={{ base: 4, md: 6 }} pb={6} ml={{ base: 0, md: "250px" }} bg="gray.50" minH="100vh">
					<Heading mb={6} fontSize="2xl" fontWeight="bold" color="gray.700">
						Facilities
					</Heading>

					<Box bg="white" p={4} borderRadius="lg" boxShadow="sm" mb={6}>
						<SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mb={3}>
							<Input
								placeholder="Facility Name"
								size="sm"
								borderRadius="md"
								value={nameFilter}
								onChange={(e) => setNameFilter(e.target.value)}
								isDisabled={loading}
							/>
						</SimpleGrid>
						<Flex justify="space-between" flexWrap="wrap" gap={2}>
							<Button
								size="sm"
								colorScheme="green"
								leftIcon={<AddIcon />}
								onClick={() => handleOpenModal()}
								variant="outline"
							>
								Create Facility
							</Button>
							<HStack spacing={2}>
								<Button
									size="sm"
									variant="ghost"
									colorScheme="gray"
									onClick={handleClearClick}
									isDisabled={loading}
								>
									Clear Filters
								</Button>
								<Button
									size="sm"
									colorScheme="blue"
									onClick={handleSearchClick}
									isLoading={isSearching}
									leftIcon={<SearchIcon />}
								>
									Search
								</Button>
							</HStack>
						</Flex>
					</Box>

					<Box overflowX="auto" borderRadius="lg" bg="white" boxShadow="md">
						<Table variant="simple" colorScheme="gray" size="sm">
							<Thead bg="gray.100">
								{table.getHeaderGroups().map((headerGroup) => (
									<Tr key={headerGroup.id}>
										{headerGroup.headers.map((header) => (
											<Th key={header.id}>
												{flexRender(
													header.column.columnDef.header,
													header.getContext()
												)}
											</Th>
										))}
									</Tr>
								))}
							</Thead>
							<Tbody>
								{table.getRowModel().rows.map((row) => (
									<Tr
										key={row.id}
										_hover={{ bg: 'blue.100' }}
										transition="background 0.2s ease"
									>
										{row.getVisibleCells().map((cell) => (
											<Td key={cell.id} fontSize="sm" py={2}>
												{flexRender(
													cell.column.columnDef.cell,
													cell.getContext()
												)}
											</Td>
										))}
									</Tr>
								))}
							</Tbody>
						</Table>
					</Box>

					<Tabs count={count} offset={offset} onOffsetChange={setOffset} />

					<Modal isOpen={isModalOpen} onClose={handleCloseModal} isCentered>
						<ModalOverlay />
						<ModalContent>
							<ModalHeader>
								{selectedFacility ? 'Edit Facility' : 'Create Facility'}
							</ModalHeader>
							<ModalCloseButton />
							<ModalBody pb={6}>
								<Stack spacing={4}>
									<FormControl isRequired>
										<FormLabel>Name</FormLabel>
										<Input
											value={name}
											onChange={(e) => setName(e.target.value)}
										/>
									</FormControl>
									<FormControl isRequired>
										<FormLabel>Address</FormLabel>
										<Input
											value={address}
											onChange={(e) => setAddress(e.target.value)}
										/>
									</FormControl>
									<FormControl isRequired>
										<FormLabel>City</FormLabel>
										<Input
											value={city}
											onChange={(e) => setCity(e.target.value)}
										/>
									</FormControl>
									<FormControl isRequired>
										<FormLabel>State</FormLabel>
										<Select
											placeholder="Select state"
											value={state}
											onChange={(e) => setState(e.target.value)}
										>
											{states.map((s) => (
												<option key={s.code} value={s.code}>
													{s.name}
												</option>
											))}
										</Select>
									</FormControl>
									<FormControl isRequired>
										<FormLabel>Zip</FormLabel>
										<Input
											value={zip}
											onChange={(e) => setZip(e.target.value)}
											maxLength={15}
										/>
									</FormControl>
									<FormControl>
										<FormLabel>Price Group</FormLabel>
										<Select
											placeholder="None (use default pricing)"
											value={priceGroupId}
											onChange={(e) => setPriceGroupId(e.target.value)}
										>
											{priceGroups.map((g) => (
												<option key={g.id} value={g.id}>
													{g.name}
												</option>
											))}
										</Select>
									</FormControl>
									<FormControl>
										<FormLabel>Fishbowl Customer Name</FormLabel>
										<Input
											placeholder="Exact Fishbowl customer name"
											value={fishbowlCustomerName}
											onChange={(e) => setFishbowlCustomerName(e.target.value)}
											maxLength={60}
										/>
									</FormControl>
									<FormControl>
										<FormLabel>Fishbowl Tax Rate</FormLabel>
										<Input
											placeholder="e.g. Non-Taxable"
											value={fishbowlTaxRateName}
											onChange={(e) => setFishbowlTaxRateName(e.target.value)}
											maxLength={31}
										/>
									</FormControl>
								</Stack>
							</ModalBody>
							<ModalFooter>
								<Button
									colorScheme="blue"
									mr={3}
									onClick={() => handleSave()}
									isDisabled={!name.trim() || !state.trim() || !address.trim() || !city.trim() || !zip.trim()}
								>
									{selectedFacility ? 'Update' : 'Save'}
								</Button>
								<Button variant="ghost" onClick={handleCloseModal}>
									Cancel
								</Button>
							</ModalFooter>
						</ModalContent>
					</Modal>

					<Modal isOpen={isDeleteModalOpen} onClose={handleCloseDeleteModal} isCentered>
						<ModalOverlay />
						<ModalContent>
							<ModalHeader>Delete Facility</ModalHeader>
							<ModalCloseButton />
							<ModalBody>
								Are you sure you want to delete {facilityToDelete?.name}?
							</ModalBody>
							<ModalFooter>
								<Button mr={3} onClick={handleCloseDeleteModal}>Cancel</Button>
								<Button colorScheme="red" onClick={confirmDelete}>Delete</Button>
							</ModalFooter>
						</ModalContent>
					</Modal>
				</Box>
			) : (
				<Box ml={{ base: 0, md: "250px" }} p={10} display="flex" justifyContent="center">
					<Spinner size="xl" thickness="4px" speed="0.65s" color="blue.500" />
				</Box>
			)}
		</>
	);
};

const mapStateToProps = (state) => ({
	facilities: state.facilities.facilities,
	loading: state.facilities.loading,
	count: state.facilities.count,
});

const mapDispatchToProps = {
	getAllFacilities,
	saveFacility,
	editFacility,
};

export default connect(mapStateToProps, mapDispatchToProps)(Facilities);
